import React from "react";
import bannersJson from "./Banners.json";
import Banner2 from "./Banner2.jsx";
import Flickity from "flickity";
import "flickity/css/flickity.css";
import "./Banners.less";

const AUTOPLAY_MS = 6000;

export class Banners extends React.Component {
    constructor(props) {
        super(props);

        this.setData = this.setData.bind(this);
        this.handleBannerClick = this.handleBannerClick.bind(this);

        // Ref to outer .banner element
        this.bannersDiv = React.createRef();
        // Ref to the carousel container that Flickity will own
        this.carouselRef = React.createRef();

        this.flickity = null;
        this._initialIndex = 0;
        this._responsiveCellAlign = null;
        this._onResponsiveResize = null;

        this.state = {
            bannersJson: bannersJson,
            renderBanners: false,
            selectedIndex: 0,
            activeBanners: null,
        };
    }

    // ---------------------------------------------------------------------------
    // Lifecycle
    // ---------------------------------------------------------------------------

    componentDidMount() {
        if (this.props.carouselResponsive) {
            this._onResponsiveResize = () => {
                const newAlign = window.innerWidth >= 768 ? 'left' : 'center';
                if (newAlign !== this._responsiveCellAlign) {
                    // Sačuvaj trenutni index prije re-inita Flickityja
                    if (this.flickity) {
                        this._initialIndex = this.flickity.selectedIndex || 0;
                    }
                    this.initFlickity();
                }
            };
            window.addEventListener('resize', this._onResponsiveResize);
        }

        // Ako su banneri proslijeđeni kroz props, koristi ih direktno
        if (this.props.bannersData && this.props.bannersData.length > 0) {
            this.setData(this.props.bannersData);
            return;
        }
        this.getBanners();
    }

    shouldComponentUpdate(nextProps, nextState) {
        return (
            nextProps.userChecked !== this.props.userChecked ||
            nextState.selectedIndex !== this.state.selectedIndex ||
            nextState.activeBanners !== this.state.activeBanners
        );
    }

    componentDidUpdate(prevProps, prevState) {
        // User auth state changed → reload banners
        if (
            this.props.user !== prevProps.user ||
            this.props.userChecked !== prevProps.userChecked
        ) {
            this.destroyFlickity();
            this.setState({ activeBanners: [] });
            // Ako imamo pre-fetchane bannere, koristi njih umjesto API poziva
            if (this.props.bannersData && this.props.bannersData.length > 0) {
                this.setData(this.props.bannersData);
            } else {
                this.getBanners();
            }
            return;
        }

        // Active banners list (re)populated → (re)init Flickity
        if (
            this.state.activeBanners &&
            this.state.activeBanners !== prevState.activeBanners &&
            this.state.activeBanners.length > 0
        ) {
            this.initFlickity();
        }
    }

    componentWillUnmount() {
        if (this._onResponsiveResize) {
            window.removeEventListener('resize', this._onResponsiveResize);
            this._onResponsiveResize = null;
        }
        if (this.dataPromise?.cancel) {
            this.dataPromise.cancel();
        }
        this.destroyFlickity();
        clearTimeout(this.lastBannerShownTimer);
    }

    // ---------------------------------------------------------------------------
    // Data fetching
    // ---------------------------------------------------------------------------

    getBanners() {
        if (!this.props.userChecked) return;

        this.dataPromise = casino.utils.makeCancelablePromise(
            casino.repos.bannerRepo.list(true)
        );
        this.dataPromise.promise.then(this.setData, function (reason) { });
    }

    setData(data) {
        let activeBanners = [];
        const baseUrl = this.props.absoluteBaseUrl || '';
        const ensureAbsolute = (url) => {
            if (!url || !baseUrl) return url;
            return url.startsWith('http') ? url : baseUrl + url;
        };

        data.forEach(b => {
            let banner = this.state.bannersJson.find(it => it.sifra == b.sifra);
            if (banner) {

                if (b.buttonLinkMobile1) {

                    banner.link = ensureAbsolute(b.buttonLinkMobile1);
                    // Ako je URL postao apsolutan (web komponenta s absoluteBaseUrl),
                    // postavi zastavicu da se koristi <a> umjesto <NavLink>
                    if (baseUrl) banner.linkAbsolute = true;
                }

                if (b.buttonLinkMobile2) {
                    banner.link2 = ensureAbsolute(b.buttonLinkMobile2);

                    if (baseUrl) banner.link2Absolute = true;
                }

                if (this.props.user && banner.sifra == "free-spins") {
                    return;
                }
                if (this.props.user && banner.sifra == "first-deposit-bonus") {
                    return;
                } else {
                    activeBanners.push(banner);
                }
            }
        });

        let bannerPromocija = casino.utils.getUrlParameters("promocija")["promocija"];
        if (bannerPromocija && activeBanners.length > 0) {
            activeBanners = activeBanners.sort(
                (a, b) => (b.sifra === bannerPromocija) - (a.sifra === bannerPromocija)
            );
        }

        // Determine starting slide index from localStorage
        let initialIndex = 0;
        if (!casino.config.rotateBannersWithoutMemorising) {
            const lastBannerShown = parseInt(localStorage.getItem("last_banner_shown"));
            if (
                lastBannerShown >= 0 &&
                lastBannerShown < activeBanners.length - 1 &&
                Date.now() - parseInt(localStorage.getItem("page_visit_time")) < 86400000
            ) {
                initialIndex = lastBannerShown + 1;
            } else {
                localStorage.setItem("page_visit_time", Date.now());
                localStorage.setItem("last_banner_shown", 999);
            }
        } else {
            localStorage.removeItem("last_banner_shown");
        }

        this._initialIndex = initialIndex;

        this.setState({
            activeBanners: activeBanners,
            renderBanners: true,
            selectedIndex: initialIndex,
        });
    }

    // ---------------------------------------------------------------------------
    // Flickity init / teardown
    // ---------------------------------------------------------------------------

    initFlickity() {
        this.destroyFlickity();
        if (!this.carouselRef.current) return;

        const cellAlign = (this.props.carouselResponsive && window.innerWidth >= 768)
            ? 'left'
            : 'center';
        this._responsiveCellAlign = cellAlign;

        const flickityOptions = Object.assign({
            cellAlign: 'center',
            wrapAround: true,
            prevNextButtons: false,
            pageDots: false,
            draggable: true,
            contain: false,
            autoPlay: AUTOPLAY_MS,
            pauseAutoPlayOnHover: false,
            initialIndex: this._initialIndex || 0,
            selectedAttraction: 0.03,
            friction: 1,
            selectedAttraction: 0.3
        }, this.props.carouselOptions || {}, {
            cellAlign: cellAlign
        });

        this.flickity = new Flickity(this.carouselRef.current, flickityOptions);

        // React to slide changes triggered by Flickity (drag, autoplay, .select())
        this.flickity.on('change', (index) => {
            const banner = this.state.activeBanners?.[index];
            if (
                banner &&
                (banner.sifra === "tech4bet-cl-utrka" || banner.sifra === "kajot-cl-utrka")
            ) {
                casino.utils.restartGif('.banner--gif--image');
            }

            if (!casino.config.rotateBannersWithoutMemorising) {
                clearTimeout(this.lastBannerShownTimer);
                this.lastBannerShownTimer = setTimeout(() => {
                    localStorage.setItem("last_banner_shown", index);
                }, 4000);
            }

            this.setState({ selectedIndex: index });
        });
    }

    destroyFlickity() {
        if (this.flickity) {
            try {
                this.flickity.destroy();
            } catch (e) { /* ignore */ }
            this.flickity = null;
        }
    }

    // ---------------------------------------------------------------------------
    // UI helpers
    // ---------------------------------------------------------------------------

    showBanners() {
        return this.state?.activeBanners && this.state.renderBanners;
    }

    handleBannerClick = (index, b) => {
        if (this.flickity) {
            this.flickity.select(index);
        }
    };

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------

    render() {
        const { selectedIndex } = this.state;
        return (
            <>
                {/* Slider viewport */}
                <div className="banner" ref={this.bannersDiv}>
                    {this.showBanners() && (
                        <div className="banners-carousel" ref={this.carouselRef}>
                            {this.state.activeBanners.map((b, index) => {
                                // Strip any pre-existing "selected" baked into the JSON className,
                                // then add it back only for the active slide.
                                const baseClass = b.className.replace(/\bselected\b/g, '').trim();
                                const sectionClass =
                                    baseClass + (index === selectedIndex ? ' selected' : '');

                                return (
                                    <section key={index} className={sectionClass}>
                                        <Banner2
                                            {...this.props}
                                            brands={this.props.brands}
                                            titleText={b.titleText}
                                            buttonClassName={b.buttonClassName}
                                            buttonText={b.buttonText}
                                            link={b.link}
                                            linkAbsolute={b.linkAbsolute}
                                            notCenterVerticalyText={b.notCenterVerticalyText}
                                            gifImage={b.gifImage}
                                            bannerImage2={b.bannerImage2}
                                            bannerImage3={b.bannerImage3}
                                            bannerImage4={b.bannerImage4}
                                            bannerImage5={b.bannerImage5}
                                            buttonText2={b.buttonText2}
                                            link2={b.link2}
                                            link2Absolute={b.link2Absolute}
                                            smallText={b.smallText}
                                            smallText2={b.smallText2}
                                            smallText3={b.smallText3}
                                            newGames={this.props.noveIgreBanner.length > 1 && this.props.noveIgreBanner}
                                            igra1={b.igra1 && this.props.noveIgreBanner && this.props.noveIgreBanner.length > 1 && this.props.noveIgreBanner[0]}
                                            igra2={b.igra2 && this.props.noveIgreBanner && this.props.noveIgreBanner.length > 1 && this.props.noveIgreBanner[1]}
                                            firstPerson1={b.firstPerson1}
                                            firstPerson2={b.firstPerson2}
                                            gameIdButtonText={b.gameIdButtonText}
                                            gameId2={b.gameId2}
                                            tab={b.tab}
                                            najveciJackpot={b.najveciJackpot}
                                            imageTitle={b.imageTitle}
                                            bigText2={b.bigText2}
                                            bigText={b.bigText}
                                            sifra={b.sifra}
                                            jackpotProviderId={b.jackpotProviderId}
                                            gameId={b.igra1 && this.props.noveIgreBanner && this.props.noveIgreBanner.length > 1 && casino.utils.normalizeString(this.props.noveIgreBanner[0].name)}
                                            backgroundRepeaterImageUrl={b.igra1 && this.props.noveIgreBanner && this.props.noveIgreBanner.length > 1 && casino.utils.normalizeString(this.props.noveIgreBanner[0].name)}
                                            mIgra1={b.sifra == "maradona-v3" && this.props.maradonaIgreBanner && this.props.maradonaIgreBanner.length > 1 && this.props.maradonaIgreBanner[0]}
                                            mIgra2={b.sifra == "maradona-v3" && this.props.maradonaIgreBanner && this.props.maradonaIgreBanner.length > 1 && this.props.maradonaIgreBanner[1]}
                                            rive={b.rive}
                                            riveStateMachineName={b.riveStateMachineName}
                                            riveWidth={b.riveWidth || null}
                                            riveHeight={b.riveHeight || null}
                                            rive2={b.rive2}
                                            riveStateMachineName2={b.riveStateMachineName2}
                                            riveWidth2={b.riveWidth2 || null}
                                            riveHeight2={b.riveHeight2 || null}
                                            index={index}
                                            selectedIndex={selectedIndex}
                                            activeBanners={this.state.activeBanners}
                                            iframe={b.iframe}
                                            iframeWidth={b.iframeWidth}
                                            iframeHeight={b.iframeHeight}
                                            videoURL={b.videoURL}
                                        />
                                    </section>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Navigation dots */}
                <div className="banner--location move-up-35-px-on-mobile">
                    {/* {this.showBanners() && this.state.activeBanners.map((b, index) => (
                        <span
                            key={index}
                            onClick={() => this.handleBannerClick(index, b)}
                            className={'banner-dot' + (index === selectedIndex ? ' selected' : '')}
                        />
                    ))} */}
                </div>
            </>
        );
    }
}
